'use client';

import dynamic from 'next/dynamic';
import Image from 'next/image';
import { type MessageKeys, useTranslations } from 'next-intl';
import React, { useEffect } from 'react';
import VideoLazyLoad from '@/app/components/shared/VideoLazyLoad';
import { Button } from '@/app/components/ui/button';
import { HOME_PAGE } from '@/app/constants';
import { cn, reportEvent } from '@/app/utils';
import { useRouter } from '@/i18n/routing';
import type { FeatureCarouselItem } from '../../_components/FeatureCarousel';
import ProductInputTool from '../../_components/ProductInputTool';
import { reportProductPageElementClick } from '../../_components/ProductInputTool/utils';

const FeatureCarousel = dynamic(() => import('../../_components/FeatureCarousel').then((mod) => mod.FeatureCarousel));
const ProductSteps = dynamic(() => import('@/app/components/shared/ProductSteps').then((mod) => mod.ProductSteps));
const UseCaseCards = dynamic(() => import('../../_components/UseCaseCards').then((mod) => mod.UseCaseCards));
const RelatedProductsSection = dynamic(() =>
  import('../../_components/RelatedProductsSection').then((mod) => mod.RelatedProductsSection),
);
const QuestionSection = dynamic(() => import('./QuestionSection').then((mod) => mod.QuestionSection));

interface SectionConfig {
  id: string;
  titleKey: MessageKeys<IntlMessages, 'textToImage.title' | 'referenceToImage.title' | 'imageEditing.title'>;
  descriptionKey: MessageKeys<
    IntlMessages,
    'textToImage.description' | 'referenceToImage.description' | 'imageEditing.description'
  >;
  redirectUrl: string;
  features: FeatureCarouselItem[];
}

export const AiImageGenerator: React.FC = () => {
  const router = useRouter();
  const t = useTranslations('ai-image-generator');

  // Hero Section 数据
  const heroFeature = {
    title: t('title'),
    description: t('subtitle'),
    videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/ai-image-generator_banner-5d3fcec3.mp4',
    mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/ai-image-generator_banner-5d3fcec3-f8800b32.mp4',
    videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/ai-image-generator_banner-98269f73.webp',
  };

  // 所有部分的配置
  const sections: SectionConfig[] = [
    {
      id: 'textToImage',
      titleKey: 'textToImage.title',
      descriptionKey: 'textToImage.description',
      redirectUrl: '/create/text2image',
      features: [
        {
          id: 'ultraHd',
          title: t('textToImage.features.ultraHd.title'),
          description: t('textToImage.features.ultraHd.description'),
          image: 'https://image01.cf.vidu.studio/vidu/media-asset/ultraHd-878128f5-05cdc636.webp',
        },
        {
          id: 'fastGeneration',
          title: t('textToImage.features.fastGeneration.title'),
          description: t('textToImage.features.fastGeneration.description'),
          image: 'https://image01.cf.vidu.studio/vidu/media-asset/fastGeneration-b900bd38-d0954a08.webp',
        },
        {
          id: 'anySize',
          title: t('textToImage.features.anySize.title'),
          description: t('textToImage.features.anySize.description'),
          image: 'https://image01.cf.vidu.studio/vidu/media-asset/anySize-3bf702de-76aa1f0b.webp',
        },
      ],
    },
    {
      id: 'referenceToImage',
      titleKey: 'referenceToImage.title',
      descriptionKey: 'referenceToImage.description',
      redirectUrl: '/create/reference2image',
      features: [
        {
          id: 'mergeElements',
          title: t('referenceToImage.features.mergeElements.title'),
          description: t('referenceToImage.features.mergeElements.description'),
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/merge-9a0e5ab6.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/merge-9a0e5ab6-44db8672.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/merge-416ac336.webp',
        },
        {
          id: 'superConsistency',
          title: t('referenceToImage.features.superConsistency.title'),
          description: t('referenceToImage.features.superConsistency.description'),
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/consistency-1-f9719ddd.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/consistency-1-f9719ddd-7269e35f.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/consistency-1-837c2d48.webp',
        },
        {
          id: 'anyStyle',
          title: t('referenceToImage.features.anyStyle.title'),
          description: t('referenceToImage.features.anyStyle.description'),
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/purposes-90fff2ce.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/purposes-90fff2ce-6d90d365.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/purposes-6d0d0125.webp',
        },
      ],
    },
    {
      id: 'imageEditing',
      titleKey: 'imageEditing.title',
      descriptionKey: 'imageEditing.description',
      redirectUrl: '/create/image-edit',
      features: [
        {
          id: 'editContent',
          title: t('imageEditing.features.editContent.title'),
          description: t('imageEditing.features.editContent.description'),
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-5548ec43.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-5548ec43-57356e4b.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-bfb353ef.webp',
        },
        {
          id: 'resize',
          title: t('imageEditing.features.resize.title'),
          description: t('imageEditing.features.resize.description'),
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-5548ec43.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-5548ec43-57356e4b.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-bfb353ef.webp',
        },
        {
          id: 'upscale',
          title: t('imageEditing.features.upscale.title'),
          description: t('imageEditing.features.upscale.description'),
          videoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-5548ec43.mp4',
          mobileVideoSrc: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-5548ec43-57356e4b.mp4',
          videoPoster: 'https://image01.cf.vidu.studio/vidu/media-asset/image-editing-bfb353ef.webp',
        },
      ],
    },
  ];

  useEffect(() => {
    reportEvent('feature_page_view', {
      from_page: 'image generator',
    });
  }, []);

  // 处理生成按钮点击
  const handleGenerateClick = (redirectUrl: string = HOME_PAGE) => {
    reportEvent('element_click', {
      element_name: 'generate-now',
      page: 'ai-image-generator',
    });
    reportProductPageElementClick('generate now');
    router.push(redirectUrl);
  };

  // 步骤数据
  const stepItems = [
    {
      index: 1,
      title: t('step.step1.title'),
      description: t('step.step1.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step1-ba1c1b49.webp',
    },
    {
      index: 2,
      title: t('step.step2.title'),
      description: t('step.step2.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step2-97fb7451.webp',
    },
    {
      index: 3,
      title: t('step.step3.title'),
      description: t('step.step3.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step3-0b8c40fb.webp',
    },
  ];

  // Why Choose 优势项配置
  const advantages: {
    id: string;
    icon: string;
    titleKey: MessageKeys<
      IntlMessages,
      | 'whyChoose.advantages.ultraHd.title'
      | 'whyChoose.advantages.consistency.title'
      | 'whyChoose.advantages.editing.title'
      | 'whyChoose.advantages.imageToVideo.title'
    >;
    descriptionKey: MessageKeys<
      IntlMessages,
      | 'whyChoose.advantages.ultraHd.description'
      | 'whyChoose.advantages.consistency.description'
      | 'whyChoose.advantages.editing.description'
      | 'whyChoose.advantages.imageToVideo.description'
    >;
  }[] = [
    {
      id: 'ultraHd',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/ultraHd-d4ad8cd8.webp',
      titleKey: 'whyChoose.advantages.ultraHd.title',
      descriptionKey: 'whyChoose.advantages.ultraHd.description',
    },
    {
      id: 'consistency',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/consistency-796cc234.webp',
      titleKey: 'whyChoose.advantages.consistency.title',
      descriptionKey: 'whyChoose.advantages.consistency.description',
    },
    {
      id: 'editing',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/editing-0399f69a.webp',
      titleKey: 'whyChoose.advantages.editing.title',
      descriptionKey: 'whyChoose.advantages.editing.description',
    },
    {
      id: 'imageToVideo',
      icon: 'https://image01.cf.vidu.studio/vidu/media-asset/imageToVideo-74414742.webp',
      titleKey: 'whyChoose.advantages.imageToVideo.title',
      descriptionKey: 'whyChoose.advantages.imageToVideo.description',
    },
  ];

  // Use Cases 卡片配置
  const useCaseCards = [
    {
      id: 'creators',
      title: t('useCases.cards.creators.title'),
      description: t('useCases.cards.creators.description'),
      image: 'https://cdn.sanity.io/images/vkvs8tfg/production/d81f3a59ba6cd4e40279e6eca2ebd2bcaf452596-620x440.png',
    },
    {
      id: 'design',
      title: t('useCases.cards.design.title'),
      description: t('useCases.cards.design.description'),
      image: 'https://image01.cf.vidu.studio/vidu/media-asset/step2-9ede3d82.webp',
    },
    {
      id: 'ecommerce',
      title: t('useCases.cards.ecommerce.title'),
      description: t('useCases.cards.ecommerce.description'),
      image: 'https://cdn.sanity.io/images/vkvs8tfg/production/1ef5f7beba75a255f431589ea9c260c72f4d039b-620x440.png',
    },
  ];

  return (
    <div className="flex min-h-screen w-full flex-col bg-black text-white">
      {/* Hero Section */}
      <section className="pt-[164px] sm:pt-16">
        <div className="mx-auto flex flex-col items-center px-4">
          <div className="w-[57vw] text-center">
            <h1 className="mb-4 font-semibold text-[48px] leading-tight sm:text-[36px]">{heroFeature.title}</h1>
            <p className="mb-12 font-normal text-xl sm:mb-6 sm:text-base">{heroFeature.description}</p>
          </div>
          <div className="mx-auto min-h-[227px] w-full max-w-[57vw] md:max-w-full">
            <ProductInputTool />
          </div>
          <div className="mt-12 w-[74vw] overflow-hidden rounded-xl sm:w-full">
            <VideoLazyLoad
              videoSrc={heroFeature.videoSrc}
              mobileVideoSrc={heroFeature.mobileVideoSrc}
              poster={heroFeature.videoPoster}
              priority
            />
          </div>
        </div>
      </section>

      {/* Feature Sections */}
      {sections.map((section, index) => (
        <FeatureCarousel
          className="pt-40"
          key={section.id}
          title={t(section.titleKey)}
          description={t(section.descriptionKey)}
          features={section.features}
          buttonText={t('buttons.generateNow')}
          onButtonClick={() => handleGenerateClick(section.redirectUrl)}
          reverseLayout={index % 2 === 1}
        />
      ))}

      {/* Steps Section */}
      <ProductSteps
        title={t('step.title')}
        items={stepItems}
        page="ai-image-generator"
        theme="dark"
        buttonText={t('buttons.stepCta')}
        redirectUrl="/create/text2image"
        buttonClassName="px-10 py-5"
      />

      {/* Why Choose Section */}
      <section className="w-full bg-black pt-28 sm:pt-6">
        <div className="mx-auto max-w-[1440px] px-8 py-12 text-center md:px-4 md:py-8">
          <div className="mb-16 text-center">
            <h2 className="font-semibold text-[40px] leading-tight sm:text-[36px]">{t('whyChoose.title')}</h2>
          </div>
          <div className="flex justify-between sm:flex-wrap sm:justify-center sm:gap-y-8">
            {advantages.map((advantage, index) => {
              return (
                <React.Fragment key={advantage.id}>
                  <div className="flex w-[20%] flex-col items-center text-center sm:w-[48%]">
                    <div className="mb-4 h-16 w-20 text-center sm:mb-1">
                      <img
                        className="mx-auto size-16 sm:size-12"
                        src={advantage.icon}
                        alt={t(advantage.titleKey)}
                        width={64}
                        height={64}
                        loading="lazy"
                        decoding="async"
                      />
                    </div>

                    <div className="mx-auto min-w-[110px] whitespace-pre-line text-center font-medium text-base text-white leading-5 sm:text-xs">
                      {t(advantage.titleKey)}
                    </div>
                    <p className="mt-2 text-center font-normal text-sm text-white/60 sm:text-xs">
                      {t(advantage.descriptionKey)}
                    </p>
                  </div>

                  {index < advantages.length - 1 && (
                    <div className={cn('flex items-center', index === 1 && 'sm:hidden')}>
                      <div className="h-[60px] w-[2px] bg-white/20" />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </section>

      {/* Use Cases Section */}
      <UseCaseCards
        title={t('useCases.title')}
        subtitle={t('useCases.subtitle')}
        cards={useCaseCards}
        buttonText={t('buttons.useCaseCta')}
        redirectUrl="/create/text2image"
        page="ai-image-generator"
      />

      {/* Related Products Section */}
      <RelatedProductsSection currentSlug="ai-image-generator" />

      {/* FAQ Section */}
      <QuestionSection />

      {/* CTA Banner */}
      <section className="relative mb-[160px] h-[364px] w-full sm:h-[219px]">
        <Image
          src="https://image01.cf.vidu.studio/vidu/media-asset/CTA_Banner-0d6559ab.webp"
          alt="Vidu AI image generator call to action banner"
          width={1920}
          height={364}
          className="h-full w-full object-cover"
          loading="lazy"
        />

        {/* 内容覆盖层 */}
        <div className="absolute inset-0 z-10 flex items-center justify-center">
          <div className="mx-auto max-w-4xl px-4 text-center sm:px-8">
            <h2 className="mb-10 font-bold text-[48px] sm:font-semibold sm:text-[32px]">{t('cta.title')}</h2>
            <Button
              className="h-[60px] min-w-[224px] rounded-[100px] bg-[#0086FF] px-10 py-5 font-medium text-white text-xl shadow-lg transition-all hover:shadow-xl sm:h-12 sm:w-[195px] sm:py-4 sm:text-base"
              onClick={() => handleGenerateClick('/create/reference2image')}
            >
              {t('buttons.ctaCta')}
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};
